interface DraftMarkdownInput {
  title?: string | null;
  versionTitle?: string | null;
  executiveMarkdown?: string | null;
  implementationMarkdown?: string | null;
  clarifiedBrief?: string | null;
  resolvedRequirements?: string[] | null;
  assumptions?: string[] | null;
}

function markdownList(items?: string[] | null, fallback = 'TBD'): string {
  const cleanItems = (items || []).map((item) => item.trim()).filter(Boolean);
  if (!cleanItems.length) return `- ${fallback}`;
  return cleanItems.map((item) => `- ${item}`).join('\n');
}

function uniqueNonEmpty(items: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  items.forEach((item) => {
    const cleaned = String(item || '').trim();
    if (!cleaned) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    values.push(cleaned);
  });
  return values;
}

function extractAssumptionsFromClarifiedBrief(clarifiedBrief?: string | null): string[] {
  const text = clarifiedBrief?.trim();
  if (!text) return [];

  const assumptions: string[] = [];
  text.split('\n').forEach((line) => {
    const match = line.match(/^\s*-\s*(?:Assumptions|Low[- ]risk assumptions|Running assumptions):\s*(.+)$/i);
    if (!match) return;
    match[1].split(';').forEach((item) => {
      const cleaned = item
        .replace(/^(?:Ready|Partial)\s*-\s*Assumptions captured:\s*/i, '')
        .trim();
      if (cleaned) assumptions.push(cleaned);
    });
  });
  return uniqueNonEmpty(assumptions);
}

function fallbackAssumptionsForDraft(input: DraftMarkdownInput, productTitle: string): string[] {
  const hasDraftContext = Boolean(
    input.clarifiedBrief?.trim()
    || input.resolvedRequirements?.some((item) => item.trim())
  );
  if (!hasDraftContext) return [];

  const product = productTitle || 'this product';
  return [
    `Assume ${product} should use existing Eliza Platform product, authentication, permission, and UI patterns because no separate platform approach has been specified; validate the architecture fit before implementation.`,
    `Assume success for ${product} should be measured with adoption, workflow completion, and error-rate signals because exact launch thresholds may still be missing from the conversation; validate target metrics with stakeholders before release.`,
    `Assume ${product} should include standard loading, empty, invalid-input, permission, and transient-error states unless custom state behavior is specified later; validate copy and recovery behavior during QA.`,
  ];
}

export function buildDraftMarkdown(input: DraftMarkdownInput): string {
  if (input.executiveMarkdown?.trim()) return input.executiveMarkdown;
  if (input.implementationMarkdown?.trim()) return input.implementationMarkdown;

  const productTitle = input.title?.trim() || input.versionTitle || 'Untitled Product Spec';
  const clarifiedBrief = input.clarifiedBrief?.trim();
  const resolvedRequirements = input.resolvedRequirements || [];
  const explicitAssumptions = uniqueNonEmpty([
    ...(input.assumptions || []),
    ...extractAssumptionsFromClarifiedBrief(clarifiedBrief),
  ]);
  const assumptions = explicitAssumptions.length
    ? explicitAssumptions
    : fallbackAssumptionsForDraft(input, productTitle);
  const assumptionsMarkdown = assumptions.length
    ? markdownList(assumptions)
    : [
      '- List assumptions made beyond explicit user input, including the missing input or inferred context that caused each assumption.',
      '- Include the validation needed before or during implementation.',
    ].join('\n');

return `# Product Requirements Document
## ${productTitle}

## Table of Contents

1. [Executive Summary](#executive-summary)
2. [Problem Statement](#problem-statement)
3. [Goals and Non-Goals](#goals-and-non-goals)
4. [Target Users](#target-users)
5. [User Stories](#user-stories)
6. [Feature Details](#feature-details)
7. [Technical Requirements](#technical-requirements)
8. [Dependencies](#dependencies)
9. [Acceptance Criteria](#acceptance-criteria)
10. [Success Metrics](#success-metrics)
11. [Assumptions](#assumptions)
12. [Implementation Questions](#implementation-questions)

## Executive Summary

${clarifiedBrief || 'Summarize the product direction, primary user, first release scope, and expected business outcome.'}

## Problem Statement

- Describe the user problem this feature solves.
- Explain why the current workflow is painful, slow, unclear, risky, or incomplete.

## Goals and Non-Goals

### Goals

${markdownList(resolvedRequirements, 'Add the launch goals captured from the clarification chat.')}

### Non-Goals

- Document intentionally deferred behavior for v1.

## Target Users

- Primary user: TBD
- Secondary users or reviewers: TBD

## User Stories

- As a user, I want to complete the first workflow end-to-end so I can get value from the feature.
- As a reviewer or admin, I want enough visibility into status and errors so I can trust the result.

## Feature Details

### Core Workflow

- Entry point: TBD
- Main action: TBD
- Result or output: TBD
- Empty, loading, error, and completed states: TBD

### Product Behavior

${markdownList(resolvedRequirements, 'Add product behavior decisions from the clarification chat.')}

## Technical Requirements

- Use existing Eliza Platform patterns where possible.
- Preserve tenant, workspace, role, and source boundaries where relevant.
- Keep generated output traceable to the decisions captured in chat.

## Dependencies

- Existing product area, data source, integration, or workflow dependencies: TBD
- Provider, model, or repository exploration dependencies: TBD

## Acceptance Criteria

- The first user workflow can be completed end-to-end.
- Required states are visible to users.
- Error and fallback behavior is clear.
- Saved records, generated output, or handoff artifacts match the decisions captured in chat.

## Success Metrics

- Primary success metric: TBD
- Supporting quality or adoption metric: TBD

## Assumptions

${assumptionsMarkdown}

## Implementation Questions

- No implementation questions captured yet.
`;
}
